import * as React from "react";

import "./general-tab.scss";

import {cst, cstAssign}          from "@Common/react/chain-services";
import {consumeContext}          from "@Common/react/context";
import {Load}                    from "@Common/react/load";
import {scopedState}             from "@Common/react/scoped-state";
import {Types}                   from "@Common/types";
import {SearchableSelect}        from "@Common/ui-components/searchable-select/searchable-select";
import {Responsive}              from "@Common/ui-components/responsive";
import {sortMulti}               from "@Common/utils/collections";
import {chain}                   from "@Common/utils/fs";
import {oMapToArr}               from "@Common/utils/objects";
import {FloatingInput}           from "@Xpresso/routes/account/common/floating-labels/floating-input";
import {COMPANY_LEVEL}           from "@Xpresso/routes/common/logic/user-level";
import {ModalLayout}             from "@Xpresso/routes/common/modal-layout/modal-layout";

import {UserCreateTabs,
        goToNextTab,
        controlInput}            from "../../user-create-tabs";
interface VarStack {
    apis       : Types.Apis.Loaders.XpressoLoadApis,
    userLevels : Types.Apis.Loaders.LoadUserLevelsNext,
    isInternalUser : boolean,
    branchList : Types.Common.React.Load2.Load2Next<Types.Apis.Payloads.Next.GetBranchListResponse>,
    agentFields: Array<{
        label: string;
        mapping: string;
        maxLength: number;
    }>,
    responsive  : any,
}

export const GeneralTab = ({step, modal, agent}) : ({
    step: Types.Common.React.UseState.UseStateNext<number>, 
    modal: Types.Common.UiComponents.ModalService3.ModalService3RootNext<void>, 
    agent: Types.Common.React.UseState.UseStateNext<string>,
}) => cst<VarStack>(
    consumeContext("apis"),
    consumeContext("userLevels"),
    cstAssign("agentFields", (_, next) => next(getAgentFields())),
    cstAssign("branchList", ({apis, userLevels}, next) => Load({
        fetch: () => userLevels.userLevel === COMPANY_LEVEL ? apis.next.getBranchList() : null,
        next,
    })),
    cstAssign("isInternalUser", ({userLevels}, next) => next(userLevels?.internalUser)),
    cstAssign("responsive", (_, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>),
    ({userLevels, responsive}, next) => (
        ModalLayout({
            title: "Create New User",
            content: (
                <>
                    {responsive.ge("md") && UserCreateTabs({step})}
                    {next()}
                </>
            ),
            buttons: [
                {
                    label: "Cancel",
                    onClick: () => {
                        modal.close();
                    },
                },
                {
                    label: "Next",
                    primary: true,
                    disabled: missingRequiredData({agent, userLevels}),
                    onClick: () => goToNextTab(step),
                },
            ]
        })
    ),
    ({agentFields, branchList, isInternalUser, userLevels}) => (        
        <div className="general-tab-o4r" >
            {userLevels.userLevel === COMPANY_LEVEL && <div className="input-card" key='idBranch'>               
                {SearchableSelect({
                    getSublabel: (branch) => (<>
                        <div>
                            {[
                                branch.branchAddress1,
                                branch.branchAddress2,
                                branch.branchCity,
                                branch.branchState,
                                branch.branchZip,
                            ].filter(Boolean).join(", ")}
                        </div>
                        <div
                            xd-qa-type="dropdown_item"
                            xd-qa-name={`Office ${branch.idBranch}`}
                        >
                            {branch.branchLinkId} {isInternalUser && `| Internal ID: ${branch.idBranch}`}
                        </div>
                    </>),
                    list: chain(
                        branchList ?? [],
                        (_) => sortMulti(_, [
                            (branch) => branch["branchName"]?.toLowerCase(),
                        ]),
                    ),
                    loading: !branchList,
                    placeholder: "Select an office",
                    getLabel: (branch) => chain(
                        branch.branchName || branch.branchLinkId,
                        (label) => <span title={label} xd-qa-type="dropdown_item" xd-qa-name={`Office ${label}`}>{label}</span>
                    ),
                    mapSearchAsItemFilter: (search:string) => {
                        const searchLowercase = search.toLowerCase();
                        return (branch) => (isInternalUser && branch.idBranch.includes(searchLowercase)) || [
                            "branchName",
                            "branchLinkId",
                            "branchAddress1",
                            "branchAddress2",
                            "branchCity",
                            "branchState",
                        ].some(field => branch[field].toLowerCase().includes(searchLowercase))
                    },
                    onSelect: async (branch) => {
                        scopedState(agent, ['inputs', 'idBranch']).onChange(branch.idBranch);
                    },
                    isSelected: (branch) => scopedState(agent, ['inputs', 'idBranch']).value === branch.idBranch,

                    className: "office",
                    searchLabel: "Search an Office",
                    xdQaName: "Modal Office Dropdown",
                    scrollCloses: false,
                })}
                <div className="label-select" xd-qa-type="label" xd-qa-name="Office">
                    Office
                </div>
                <div className="required">{requiredFields.includes('idBranch') && "*"}</div>
            </div>}
            {agentFields.map((field) => {
                const props = {
                    value: scopedState(agent, ['inputs', field.mapping]).value || controlInput,
                    onChange: (e) => {
                        const value = e.target.value; 
                        scopedState(agent, ['errors', field.mapping]).onChange(false);
                        scopedState(agent, ['inputs', field.mapping]).onChange(value);
                    },
                    className: "value",
                    maxLength: field.maxLength,
                    "xd-qa-type": "textbox",
                    "xd-qa-name": field.label,
                }
                return(
                    <React.Fragment key={field.mapping}>
                        <FloatingInput
                            classContainer="input-card"
                            classLabel="label"
                            textLabel={field.label}
                            props={props}
                            required={requiredFields.includes(field.mapping)}
                        />
                        {agent.value.errors[field.mapping] && (
                            <div className="errorMessage" >{agent.value.errors[field.mapping]}</div>
                        )}
                    </React.Fragment>
                )
            })}
        </div>
    )
);

const requiredFields = [
    "agentNameFirst",
    "agentNameLast",
    "agentPhone1",
    "agentEmail",
    "idBranch"
];

export const getAgentFields = () => ([
    {
        "label": "First Name", 
        "mapping": "agentNameFirst", 
        "maxLength": 50
    },
    {
        "label": "Middle Name", 
        "mapping": "agentNameMiddle", 
        "maxLength": 50
    },
    {
        "label": "Last Name",
        "mapping": "agentNameLast",
        "maxLength": 50
    },
    {
        "label": "Phone 1",
        "mapping": "agentPhone1", 
        "maxLength": 25
    },
    {
        "label": "Phone 2",
        "mapping": "agentPhone2",
        "maxLength": 25
    },
    {
        "label": "Email",
        "mapping": "agentEmail",
        "maxLength": 60
    },
    {
        "label": "Link ID",
        "mapping": "agentLinkId",
        "maxLength": 60
    },
]);

const missingRequiredData = ({agent, userLevels}) => {
    return requiredFields.some((inputName) => {
        if(inputName === "idBranch") {
            return userLevels.userLevel === COMPANY_LEVEL ? !scopedState(agent, ['inputs', "idBranch"]).value : false
        } else {
            return !scopedState(agent, ['inputs', inputName]).value 
        }}) ||
    oMapToArr(scopedState(agent, ['errors']).value).some((error) => getAgentFields().map(a => a.mapping).includes(error.mapping));
}
